import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  signInWithPopup,
  signOut,
  updateProfile,
  type User as AuthProviderUser,
} from 'firebase/auth';
import type { User, UserRole } from '@kids-ai/shared-types';
import { authClient, googleProvider } from '../lib/authClient';

interface AuthContextValue {
  user: User | null;
  authProviderUser: AuthProviderUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  getIdToken: () => Promise<string | null>;
  login:        (email: string, password: string) => Promise<void>;
  signup:       (email: string, password: string, displayName: string) => Promise<void>;
  loginWithGoogle: () => Promise<{ isNewUser: boolean; email: string | null }>;
  logout:       () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  hasRole:      (...roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function mapAuthProviderUser(providerUser: AuthProviderUser): User {
  // Role defaults to 'student'. Server-side role claims will overwrite this
  // once the backend issues custom claims (see /api/v1/auth on the API).
  const claimRole = (providerUser as unknown as { _role?: UserRole })._role;
  return {
    id:          providerUser.uid,
    email:       providerUser.email ?? '',
    role:        claimRole ?? 'student',
    displayName: providerUser.displayName ?? providerUser.email?.split('@')[0] ?? 'Learner',
    createdAt:   providerUser.metadata.creationTime ?? new Date().toISOString(),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authProviderUser, setAuthProviderUser] = useState<AuthProviderUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Subscribe to auth state. Resolves the initial loading flag once we know
  // whether a session exists.
  useEffect(() => {
    const unsub = onAuthStateChanged(authClient, async (providerUser) => {
      setAuthProviderUser(providerUser);
      if (providerUser) {
        // Pull custom claims (role) from the ID token.
        try {
          const tokenResult = await providerUser.getIdTokenResult();
          const role = (tokenResult.claims.role as UserRole | undefined) ?? 'student';
          const mapped = mapAuthProviderUser(providerUser);
          setUser({ ...mapped, role });
        } catch {
          setUser(mapAuthProviderUser(providerUser));
        }
      } else {
        setUser(null);
      }
      setIsLoading(false);
    });
    return unsub;
  }, []);

  const getIdToken = useCallback(async () => {
    if (!authClient.currentUser) return null;
    return authClient.currentUser.getIdToken();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(authClient, email, password);
  }, []);

  const signup = useCallback(async (email: string, password: string, displayName: string) => {
    const cred = await createUserWithEmailAndPassword(authClient, email, password);
    if (displayName) {
      await updateProfile(cred.user, { displayName });
    }
    // Automatically trigger verification email from backend
    try {
      const token = await cred.user.getIdToken();
      await fetch('/api/v1/auth/send-verification', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error('Failed to send verification email on signup:', err);
    }
  }, []);

  const sendVerificationEmail = useCallback(async () => {
    const token = await getIdToken();
    if (!token) return;

    const res = await fetch('/api/v1/auth/send-verification', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error?.message || 'Failed to send verification email');
    }
  }, [getIdToken]);

  const loginWithGoogle = useCallback(async () => {
    const cred = await signInWithPopup(authClient, googleProvider);
    const info = getAdditionalUserInfo(cred);
    return {
      isNewUser: info?.isNewUser ?? false,
      email: cred.user.email,
    };
  }, []);

  const logout = useCallback(async () => {
    await signOut(authClient);
  }, []);

  const hasRole = useCallback((...roles: UserRole[]) => {
    if (!user) return false;
    return roles.includes(user.role);
  }, [user]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    authProviderUser,
    isAuthenticated: !!user,
    isLoading,
    getIdToken,
    login,
    signup,
    loginWithGoogle,
    logout,
    sendVerificationEmail,
    hasRole,
  }), [user, authProviderUser, isLoading, getIdToken, login, signup, loginWithGoogle, logout, sendVerificationEmail, hasRole]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
